import { StartupLoader } from '../../app/StartupLoader';
import { FormEvent, useEffect, useRef, useState } from 'react';
import type { components } from '@rconf/contracts';

import type { AccountAuthConfig } from '../../app/config';
import rconfLogoUrl from '../../assets/rconf-logo.svg';
import { LocaleSwitcher } from '../../i18n/runtime';
import { accountAuthError } from './errors.js';
import { initialAuthMethod } from './runtime.js';

type AuthMethod = 'password' | 'rendall';
type AuthStatus = 'restoring' | 'ready' | 'submitting' | 'transferring';
type LoginPayload = components['schemas']['LoginPayload'];

interface AuthState {
  status: string;
  user?: Record<string, unknown> | null;
  error?: unknown;
}

interface AuthRuntime {
  restore(): Promise<AuthState>;
  retryRestore(): Promise<AuthState>;
  loginWithPassword(credentials: LoginPayload): Promise<AuthState>;
  requestRendallCode(input: { login: string }): Promise<unknown>;
  verifyRendallCode(input: { login: string; code: string }): Promise<AuthState>;
  handoff(): string;
}

export function AccountAuthGateway({ config, runtime }: {
  config: AccountAuthConfig;
  runtime: AuthRuntime;
}) {
  const [status, setStatus] = useState<AuthStatus>('restoring');
  const [method, setMethod] = useState<AuthMethod>(() => initialAuthMethod(window.location.search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rendallLogin, setRendallLogin] = useState('');
  const [rendallCode, setRendallCode] = useState('');
  const [rendallStep, setRendallStep] = useState<'request' | 'verify'>('request');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const handoffStarted = useRef(false);

  useEffect(() => {
    let active = true;
    runtime.restore().then((state) => {
      if (!active) return;
      if (state.status === 'authenticated' && state.user) {
        startHandoff();
        return;
      }
      if (state.error) setError(accountAuthError(state.error).message);
      setStatus('ready');
    });
    return () => { active = false; };
  }, [runtime]);

  const startHandoff = () => {
    if (handoffStarted.current) return;
    handoffStarted.current = true;
    setStatus('transferring');
    setError('');
    setMessage('Сессия подтверждена. Открываем личный кабинет…');
    runtime.handoff();
  };

  const selectMethod = (nextMethod: AuthMethod) => {
    if (status === 'submitting') return;
    setMethod(nextMethod);
    setError('');
    setMessage('');
  };

  const retryRestore = async () => {
    setStatus('restoring');
    setError('');
    const state = await runtime.retryRestore();
    if (state.status === 'authenticated' && state.user) {
      startHandoff();
      return;
    }
    if (state.error) setError(accountAuthError(state.error).message);
    setStatus('ready');
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase().slice(0, 254);
    if (!normalizedEmail || !normalizedEmail.includes('@') || !password) {
      setError('Введите корректный e-mail и пароль.');
      return;
    }
    setStatus('submitting');
    setError('');
    setMessage('Проверяем данные…');
    try {
      const state = await runtime.loginWithPassword({
        email: normalizedEmail,
        password: password.slice(0, 512),
      });
      if (state.status !== 'authenticated' || !state.user) throw new Error('AUTH_STATE_INVALID');
      startHandoff();
    } catch (cause) {
      setError(accountAuthError(cause, 'password').message);
      setMessage('');
      setStatus('ready');
    }
  };

  const submitRendall = async (event: FormEvent) => {
    event.preventDefault();
    const login = rendallLogin.trim().slice(0, 160);
    if (!login) {
      setError('Введите Rendall login или ID.');
      return;
    }
    if (rendallStep === 'verify' && rendallCode.length !== 6) {
      setError('Введите 6-значный код из Rendall.');
      return;
    }

    setStatus('submitting');
    setError('');
    setMessage(rendallStep === 'verify' ? 'Проверяем код…' : 'Отправляем код…');
    try {
      if (rendallStep === 'verify') {
        const state = await runtime.verifyRendallCode({ login, code: rendallCode });
        if (state.status !== 'authenticated' || !state.user) throw new Error('AUTH_STATE_INVALID');
        startHandoff();
        return;
      }
      const result = await runtime.requestRendallCode({ login });
      const expiresIn = rendallExpiresIn(result);
      setRendallStep('verify');
      setMessage(expiresIn
        ? `Код отправлен. Введите его в течение ${expiresIn} сек.`
        : 'Код отправлен в Rendall. Введите его здесь.');
      setStatus('ready');
    } catch (cause) {
      const failure = accountAuthError(
        cause,
        rendallStep === 'verify' ? 'rendall-code' : 'rendall-request',
      );
      if (failure.retryAfter && rendallStep === 'request') {
        setRendallStep('verify');
        setMessage(`Код уже отправлен. Новый можно запросить через ${failure.retryAfter} сек.`);
      }
      setError(failure.message);
      setStatus('ready');
    }
  };

  const resetRendall = () => {
    setRendallStep('request');
    setRendallCode('');
    setMessage('');
    setError('');
  };

  const busy = status === 'restoring' || status === 'submitting' || status === 'transferring';
  if (status === 'restoring' || status === 'transferring') return <StartupLoader label={status === 'transferring' ? 'Открываем личный кабинет…' : 'Проверяем сессию…'} />;

  return (
    <main className="auth-wrapper">
      <div className="auth-box bg-white" aria-busy={busy}>
        <LocaleSwitcher className="auth-locale-switcher" />
        <div className="auth-head text-center">
          <div className="auth-brand"><img src={rconfLogoUrl} alt="" className="auth-logo" /><span>RConf</span></div>
          <h4>Вход в личный кабинет</h4>
          <p>{'Заполните данные ниже'}</p>
        </div>

        <>
          <div className="auth-methods" role="tablist" aria-label="Способ входа">
            <button type="button" className={`auth-method-chip${method === 'password' ? ' active' : ''}`} role="tab" aria-selected={method === 'password'} onClick={() => selectMethod('password')}>Логин / пароль</button>
            <button type="button" className={`auth-method-chip${method === 'rendall' ? ' active' : ''}`} role="tab" aria-selected={method === 'rendall'} onClick={() => selectMethod('rendall')}>Rendall</button>
          </div>

          {method === 'password' ? <form method="post" action="/" onSubmit={submitPassword} noValidate>
            <div className={`auth-field mb-3${email.trim() ? ' auth-field-active' : ''}`}><label htmlFor="tb-login">E-mail</label><input className="form-control form-input-bg" id="tb-login" name="USER_LOGIN" type="text" autoComplete="username" maxLength={254} autoFocus value={email} disabled={busy} onChange={(event) => setEmail(event.target.value)} /></div>
            <div className={`auth-field mb-3${password.trim() ? ' auth-field-active' : ''}`}><label htmlFor="text-password">Пароль</label><input className="form-control form-input-bg password pe-5" id="text-password" name="USER_PASSWORD" type={passwordVisible ? 'text' : 'password'} autoComplete="current-password" maxLength={512} value={password} disabled={busy} onChange={(event) => setPassword(event.target.value)} /><button type="button" className={`box-control password-visibility ${passwordVisible ? 'visible' : 'hidden'}${password.trim() ? '' : ' d-none'}`} aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'} aria-controls="text-password" aria-pressed={passwordVisible} onClick={() => setPasswordVisible((visible) => !visible)} /></div>
            <div className="form-check auth-remember"><input className="form-check-input" type="checkbox" id="USER_REMEMBER" name="USER_REMEMBER" checked={remember} onChange={(event) => setRemember(event.target.checked)} /><label className="form-check-label" htmlFor="USER_REMEMBER">Запомнить меня</label></div>
            <button className={`btn btn-primary d-block w-100${email.trim() && password && !busy ? '' : ' disabled'}`} type="submit" name="Login" id="submit-button" disabled={busy || !email.trim() || !password}>{status === 'submitting' ? 'Проверяем…' : 'Войти'}</button>
          </form> : <form method="post" action="/" onSubmit={submitRendall} noValidate>
            <div className={`auth-field mb-3${rendallLogin.trim() ? ' auth-field-active' : ''}`}><label htmlFor="rbot-login">Rendall login или ID</label><input className="form-control form-input-bg" id="rbot-login" name="RBOT_LOGIN" type="text" autoComplete="off" maxLength={160} autoFocus value={rendallLogin} disabled={busy || rendallStep === 'verify'} onChange={(event) => setRendallLogin(event.target.value)} /></div>
            {rendallStep === 'verify' ? <div className={`auth-field mb-3${rendallCode.trim() ? ' auth-field-active' : ''}`}><label htmlFor="rbot-code">Код из Rendall</label><input className="form-control form-input-bg auth-code-input" id="rbot-code" name="RBOT_CODE" type="text" inputMode="numeric" maxLength={6} autoComplete="one-time-code" value={rendallCode} disabled={busy} onChange={(event) => setRendallCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /></div> : null}
            <div className="auth-note">Мы отправим 6-значный код в Rendall. Если пользователя еще нет в RConf, профиль будет создан автоматически без компании.</div>
            {message ? <div className="auth-message" role="status">{message}</div> : null}
            <button className={`btn btn-primary d-block w-100${rendallLogin.trim() && (rendallStep !== 'verify' || rendallCode.length === 6) && !busy ? '' : ' disabled'}`} type="submit" disabled={busy || !rendallLogin.trim() || (rendallStep === 'verify' && rendallCode.length !== 6)}>{status === 'submitting' ? 'Проверяем...' : rendallStep === 'verify' ? 'Подтвердить код' : 'Получить код в Rendall'}</button>
            {rendallStep === 'verify' ? <button className="auth-inline-action" type="button" disabled={busy} onClick={resetRendall}>Отправить код заново</button> : null}
          </form>}

          {error ? <div className="auth-error" role="alert">{error}{status === 'ready' && error.startsWith('Нет соединения') ? <button type="button" className="auth-inline-action" onClick={retryRestore}>Повторить проверку</button> : null}</div> : null}
          <a className="btn btn-secondary d-block w-100" href={config.registerUrl}>Регистрация</a>
          <div className="auth-divider" />
          <a className="btn btn-light w-100 font-12 d-flex align-items-center justify-content-center" href={config.forgotPasswordUrl} id="to-recover">Забыли пароль?</a>
        </>
      </div>
    </main>
  );
}

function rendallExpiresIn(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const seconds = Number((value as { expires_in?: unknown }).expires_in || 0);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
}
